import { LookupModelSearchEvent, LookupModelSelectionEvent, Panel, TableRowDisplayEvent, TableRowExpansionEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutReceivablesHistory } from "./autogen/AutogenLayoutReceivablesHistory";
import { CompanySettings, LogManager, ModelRow } from "@mcleod/core";
import { CommonTooltips } from "@mcleod/common";
import { ReceivablesHistoryExpand } from "./ReceivablesHistoryExpand";
import { GLDistributionTableMultiSource } from "@mcleod/backoffice/src/GLDistributionTableMultiSource";

const log = LogManager.getLogger("lme.ar.ReceivablesHistory");

export class ReceivablesHistory extends AutogenLayoutReceivablesHistory {
    private companySettings: CompanySettings;

    override onLoad() {
        this.companySettings = CompanySettings.getSingleton();
        this.textboxCompanyId.visible = this.companySettings.isIntercompanyMainCompany();
        this.checkboxIsIntercompany.visible = this.companySettings.isIntercompanyMainCompany();
        this.checkboxDisplayChildBridgeAccounts.allowIndeterminate = false;
    }

    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textboxCustomerId = event.target as Textbox;
        const customerIdPopulated = textboxCustomerId.dataSource.searchRow != null &&
            !textboxCustomerId.dataSource.searchRow.isNull("customer_id");
        this.checkboxDisplayChildBridgeAccounts.enabled = customerIdPopulated;
        if (!customerIdPopulated) {
            this.checkboxDisplayChildBridgeAccounts.checked = false;
        }
    }

    textboxArReasonCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.code_type = "%C%";
    }

    tableReceivablesHistoryOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const customerId = modelRow.getString("customer_id", null);
        const labelCustomerName = tableRow.findComponentById("labelCustomerName");

        if (customerId && labelCustomerName) {
            CommonTooltips.setTooltipContentLayout(labelCustomerName, customerId, "lme/ar/CustomerQuickInfo");
        }
    }

    async tableReceivablesHistoryOnRowExpand(event: TableRowExpansionEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const expandPanel = event.expandComponentParent as Panel;
        const expandLayout = expandPanel.findComponentById("layoutReceivablesHistoryExpand") as ReceivablesHistoryExpand;
        const openItemId = modelRow.getString("id", null);
        if (openItemId) {
            // await - The mainDataSource search must complete before searching journal_sales
            await expandLayout.mainDataSource.search({ "id": openItemId }).catch(error => {
                log.error(`Failed to load expanded row data for invoice ${openItemId}:`, error);
            });

            // Trigger search for sourceJournalSales to ensure data is loaded
            const source = modelRow.getString("source", null);
            const postKey = modelRow.getString("post_key", null);
            const postModule = modelRow.getString("post_module", null);
            const companyId = modelRow.getString("company_id", null);
            expandLayout.layoutGLDistributionTable.displayCompanyColumn = this.companySettings.isIntercompanyMainCompany();
            const glDistributionTable = expandLayout.layoutGLDistributionTable as GLDistributionTableMultiSource;

            if (postKey && postModule) {
                switch (source) {
                    case "A": // Miscellaneous Billing
                    case "B": // Billing
                    case "I": // Accounting Interface
                    case "M": // Manual Entry
                        glDistributionTable.replaceMainDatasource(expandLayout.sourceJournalSales);
                        await glDistributionTable.mainDataSource.search({ override_company_id: companyId, post_key_openitem: postKey, post_module: postModule }).catch(error => {
                            log.error(`Failed to load journal sales data for receivables history open item ${openItemId}:`, error);
                        });
                        break;
                    case "C": // Cash Receipt
                        glDistributionTable.replaceMainDatasource(expandLayout.sourceJournalCash);
                        await glDistributionTable.mainDataSource.search({ override_company_id: companyId, post_key: postKey, post_module: postModule }).catch(error => {
                            log.error(`Failed to load journal cash receipts data for receivables history open item ${openItemId}:`, error);
                        });
                        break;
                    case "V": // Vehicle Maintenance
                        glDistributionTable.replaceMainDatasource(expandLayout.sourceJournalVm);
                        await glDistributionTable.mainDataSource.search({ override_company_id: companyId, post_key: postKey, post_module: postModule }).catch(error => {
                            log.error(`Failed to load journal vehicle maintenance data for receivables history open item ${openItemId}:`, error);
                        });
                        break;
                    default:
                        log.error(`Unknown source type '${source}' for receivables history open item ${openItemId}. No source data loaded.`);
                        break;
                }
            }
        }
    }
}
